import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthGuard } from '@/hooks/useAuth';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabaseClient';

interface PriceRow {
  id: number;
  rota: string;
  ilce: string;
  mesafe: number;
  vito_tek: number;
  vito_donus: number;
  e_class_tek: number;
  e_class_donus: number;
  sprinter_tek: number;
  sprinter_donus: number;
  bmw7_tek: number;
  bmw7_donus: number;
  para_birimi: string;
}

interface EditForm {
  vito_tek: string; vito_donus: string;
  e_class_tek: string; e_class_donus: string;
  sprinter_tek: string; sprinter_donus: string;
  bmw7_tek: string; bmw7_donus: string;
  para_birimi: string;
}

const ARAC_MAP: Record<string, { tekKey: keyof PriceRow; donusKey: keyof PriceRow; kapasite: number }> = {
  'Mercedes Vito': { tekKey: 'vito_tek', donusKey: 'vito_donus', kapasite: 8 },
  'Mercedes E-Sınıfı': { tekKey: 'e_class_tek', donusKey: 'e_class_donus', kapasite: 4 },
  'Mercedes Sprinter': { tekKey: 'sprinter_tek', donusKey: 'sprinter_donus', kapasite: 16 },
  'BMW 7 Serisi': { tekKey: 'bmw7_tek', donusKey: 'bmw7_donus', kapasite: 4 },
};

const ARAC_TIPLERI = Object.keys(ARAC_MAP);
const KISI_SAYILARI = Array.from({ length: 16 }, (_, i) => i + 1);
const PARA_BIRIMLERI = ['EUR', 'USD', 'TL'];

const paraSembol = (p: string) => p === 'EUR' ? '€' : p === 'USD' ? '$' : '₺';

export default function FiyatHesaplamaPage() {
  useAuthGuard();
  const navigate = useNavigate();
  const [priceTable, setPriceTable] = useState<PriceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [editingRow, setEditingRow] = useState<PriceRow | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<PriceRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Calculator state
  const [alinis, setAlinis] = useState('Antalya Havalimanı');
  const [birak, setBirak] = useState('');
  const [aracTipi, setAracTipi] = useState('');
  const [kisi, setKisi] = useState(2);
  const [tip, setTip] = useState('Tek Yön');
  const [pb, setPb] = useState('');
  const [showResult, setShowResult] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase.from('price_table').select('*').order('ilce');
    if (err) setError(err.message);
    else setPriceTable(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showSuccess = (msg: string) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); };

  const handleEdit = (row: PriceRow) => {
    setEditingRow(row);
    setEditForm({
      vito_tek: String(row.vito_tek), vito_donus: String(row.vito_donus),
      e_class_tek: String(row.e_class_tek), e_class_donus: String(row.e_class_donus),
      sprinter_tek: String(row.sprinter_tek), sprinter_donus: String(row.sprinter_donus),
      bmw7_tek: String(row.bmw7_tek), bmw7_donus: String(row.bmw7_donus),
      para_birimi: row.para_birimi,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingRow || !editForm) return;
    setSaving(true);
    const updated = {
      vito_tek: Number(editForm.vito_tek), vito_donus: Number(editForm.vito_donus),
      e_class_tek: Number(editForm.e_class_tek), e_class_donus: Number(editForm.e_class_donus),
      sprinter_tek: Number(editForm.sprinter_tek), sprinter_donus: Number(editForm.sprinter_donus),
      bmw7_tek: Number(editForm.bmw7_tek), bmw7_donus: Number(editForm.bmw7_donus),
      para_birimi: editForm.para_birimi,
    };
    const { error: err } = await supabase.from('price_table').update(updated).eq('id', editingRow.id);
    if (!err) {
      setPriceTable(prev => prev.map(r => r.id === editingRow.id ? { ...r, ...updated } : r));
      setEditingRow(null);
      setEditForm(null);
      showSuccess('Fiyatlar güncellendi.');
    } else setError(err.message);
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const idToDelete = deleteConfirm.id;
    setDeleting(true);
    setDeleteConfirm(null);

    const { data, error } = await supabase.functions.invoke('manage-data', {
      body: { action: 'delete', table: 'price_table', id: idToDelete },
    });

    setDeleting(false);

    if (error || !data?.success) {
      setError('Silinemedi: ' + (error?.message || data?.error || 'Bilinmeyen hata'));
      await fetchData();
      return;
    }
    setPriceTable(prev => prev.filter(r => r.id !== idToDelete));
    showSuccess('Fiyat satırı silindi.');
  };

  const ilceList = useMemo(() => priceTable.map(r => r.ilce), [priceTable]);

  const sonuc = useMemo(() => {
    if (!birak || !aracTipi) return null;
    const rota = priceTable.find(r => r.ilce === birak);
    if (!rota) return null;
    const arac = ARAC_MAP[aracTipi];
    if (!arac) return null;
    if (kisi > arac.kapasite) return { hata: `Bu araç tipi maksimum ${arac.kapasite} yolcu kapasitelidir.` };
    const tekFiyat = Number(rota[arac.tekKey]);
    const donusFiyat = Number(rota[arac.donusKey]);
    const fiyat = tip === 'Tek Yön' ? tekFiyat : donusFiyat;
    return { rota: rota.rota, mesafe: rota.mesafe, aracTipi, kisi, tip, fiyat, paraBirimi: pb || rota.para_birimi, tekFiyat, donusFiyat };
  }, [birak, aracTipi, kisi, tip, pb, priceTable]);

  return (
    <AdminLayout>
      {/* Edit Modal */}
      {editingRow && editForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-vip-dark border border-vip-gold/15 rounded-xl w-full max-w-lg my-4">
            <div className="flex items-center justify-between p-5 border-b border-vip-gold/10">
              <h3 className="text-base font-semibold text-white">Fiyat Düzenle — {editingRow.rota}</h3>
              <button onClick={() => { setEditingRow(null); setEditForm(null); }} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-vip-gold/10 text-gray-400 hover:text-white">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="mb-2">
                <label className="block text-xs text-gray-400 mb-1.5">Para Birimi</label>
                <select value={editForm.para_birimi} onChange={e => setEditForm(p => p ? { ...p, para_birimi: e.target.value } : p)}
                  className="bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-vip-gold/40">
                  {PARA_BIRIMLERI.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              {[
                { label: 'Vito Tek Yön', key: 'vito_tek' }, { label: 'Vito Gidiş-Dönüş', key: 'vito_donus' },
                { label: 'E-Sınıfı Tek Yön', key: 'e_class_tek' }, { label: 'E-Sınıfı Gidiş-Dönüş', key: 'e_class_donus' },
                { label: 'Sprinter Tek Yön', key: 'sprinter_tek' }, { label: 'Sprinter Gidiş-Dönüş', key: 'sprinter_donus' },
                { label: 'BMW 7 Tek Yön', key: 'bmw7_tek' }, { label: 'BMW 7 Gidiş-Dönüş', key: 'bmw7_donus' },
              ].reduce<JSX.Element[]>((rows, item, idx, arr) => {
                if (idx % 2 === 0) {
                  const next = arr[idx + 1];
                  rows.push(
                    <div key={item.key} className="grid grid-cols-2 gap-3">
                      {[item, next].filter(Boolean).map(f => (
                        <div key={f.key}>
                          <label className="block text-xs text-gray-400 mb-1">{f.label}</label>
                          <input type="number" value={(editForm as any)[f.key]}
                            onChange={e => setEditForm(p => p ? { ...p, [f.key]: e.target.value } : p)}
                            className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-vip-gold/40" />
                        </div>
                      ))}
                    </div>
                  );
                }
                return rows;
              }, [])}
              <div className="flex gap-3 pt-2">
                <button onClick={() => { setEditingRow(null); setEditForm(null); }} className="flex-1 py-2.5 rounded-md bg-vip-black border border-vip-gold/10 text-gray-400 text-sm hover:text-white whitespace-nowrap">İptal</button>
                <button onClick={handleSaveEdit} disabled={saving} className="flex-1 py-2.5 rounded-md bg-vip-gold text-vip-black font-semibold text-sm hover:bg-vip-gold-light transition-colors disabled:opacity-50 whitespace-nowrap">
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-vip-dark border border-red-500/20 rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-base font-semibold text-white text-center mb-2">Fiyat Satırını Sil</h3>
            <p className="text-sm text-gray-400 text-center mb-5"><strong className="text-white">{deleteConfirm.rota}</strong></p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-md bg-vip-black border border-vip-gold/10 text-gray-400 text-sm hover:text-white whitespace-nowrap">İptal</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 rounded-md bg-red-500/20 border border-red-500/30 text-red-400 text-sm disabled:opacity-50 whitespace-nowrap">{deleting ? 'Siliniyor...' : 'Sil'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-display font-bold text-white">Otomatik Fiyat Hesaplama</h2>
        <p className="text-sm text-gray-400 mt-1">Rota, araç tipi ve yolcu sayısına göre anında fiyat alın</p>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-4 flex items-center gap-2 text-sm text-red-400"><i className="ri-error-warning-line mr-2"></i>{error}<button onClick={() => setError('')} className="ml-auto"><i className="ri-close-line"></i></button></div>}
      {successMsg && <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-3 mb-4 text-sm text-emerald-400 flex items-center gap-2"><i className="ri-check-line"></i>{successMsg}</div>}

      {/* Calculator */}
      <div className="flex flex-col lg:flex-row gap-6 mb-6">
        <div className="w-full lg:w-1/2 bg-vip-dark border border-vip-gold/10 rounded-lg p-5 md:p-6">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-vip-gold/15 border border-vip-gold/20 flex items-center justify-center"><i className="ri-route-line text-vip-gold text-xs"></i></div>
            Transfer Bilgileri
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Alınış Noktası</label>
              <select value={alinis} onChange={e => { setAlinis(e.target.value); setShowResult(false); }}
                className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-vip-gold/40">
                {['Antalya Havalimanı','Antalya Merkez'].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Bırakış Noktası (İlçe)</label>
              <select value={birak} onChange={e => { setBirak(e.target.value); setShowResult(false); }}
                className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-vip-gold/40">
                <option value="">İlçe seçin</option>
                {ilceList.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Araç Tipi</label>
              <select value={aracTipi} onChange={e => { setAracTipi(e.target.value); setShowResult(false); }}
                className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-vip-gold/40">
                <option value="">Araç tipi seçin</option>
                {ARAC_TIPLERI.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Kişi Sayısı</label>
              <select value={kisi} onChange={e => { setKisi(Number(e.target.value)); setShowResult(false); }}
                className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-vip-gold/40">
                {KISI_SAYILARI.map(k => <option key={k} value={k}>{k} Kişi</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Transfer Türü</label>
              <div className="flex gap-2">
                {['Tek Yön','Gidiş-Dönüş'].map(t => (
                  <button key={t} onClick={() => { setTip(t); setShowResult(false); }}
                    className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-all border ${tip === t ? 'bg-vip-gold/15 text-vip-gold border-vip-gold/30' : 'bg-vip-black text-gray-400 border-vip-gold/10 hover:text-white'}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">Para Birimi (Opsiyonel)</label>
              <select value={pb} onChange={e => { setPb(e.target.value); setShowResult(false); }}
                className="w-full bg-vip-black border border-vip-gold/15 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-vip-gold/40">
                <option value="">Otomatik (Rota bazlı)</option>
                {PARA_BIRIMLERI.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <button onClick={() => { if (birak && aracTipi) setShowResult(true); }} disabled={!birak || !aracTipi}
              className="w-full mt-2 py-3 bg-vip-gold text-vip-black font-semibold rounded-md hover:bg-vip-gold-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
              <i className="ri-calculator-line mr-2"></i>Fiyat Hesapla
            </button>
          </div>
        </div>

        <div className="w-full lg:w-1/2">
          {!showResult || !sonuc ? (
            <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-8 text-center h-full flex flex-col items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-vip-gold/10 flex items-center justify-center mb-4"><i className="ri-calculator-line text-2xl text-vip-gold"></i></div>
              <h3 className="text-lg font-semibold text-white mb-2">Fiyat Hesaplama</h3>
              <p className="text-sm text-gray-400 max-w-xs">Sol taraftan transfer bilgilerini seçip "Fiyat Hesapla" butonuna tıklayın.</p>
            </div>
          ) : 'hata' in sonuc ? (
            <div className="bg-vip-dark border border-red-500/20 rounded-lg p-6">
              <p className="text-sm text-red-400"><i className="ri-error-warning-line mr-2"></i>{sonuc.hata}</p>
            </div>
          ) : (
            <div className="bg-vip-dark border border-vip-gold/20 rounded-lg p-5 md:p-6">
              <div className="text-center mb-6">
                <p className="text-xs text-gray-400 mb-1">Tahmini Transfer Ücreti</p>
                <p className="text-4xl md:text-5xl font-display font-bold text-vip-gold">{sonuc.fiyat} {paraSembol(sonuc.paraBirimi)}</p>
                <p className="text-xs text-gray-500 mt-1">{sonuc.tip} · {sonuc.kisi} Kişi</p>
              </div>
              <div className="space-y-3 mb-6">
                {[['Rota', sonuc.rota], ['Mesafe', `${sonuc.mesafe} km`], ['Araç Tipi', sonuc.aracTipi], ['Tek Yön', `${sonuc.tekFiyat} ${paraSembol(sonuc.paraBirimi)}`], ['Gidiş-Dönüş', `${sonuc.donusFiyat} ${paraSembol(sonuc.paraBirimi)}`]].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between py-2 border-b border-vip-gold/5">
                    <span className="text-sm text-gray-400">{label}</span>
                    <span className="text-sm text-white font-medium">{value}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => {
                const params = new URLSearchParams({ rota: sonuc.rota, arac: sonuc.aracTipi, kisi: String(sonuc.kisi), tip: sonuc.tip, fiyat: String(sonuc.fiyat), pb: sonuc.paraBirimi });
                navigate(`/rezervasyonlar/yeni?${params.toString()}`);
              }} className="w-full py-3 bg-vip-gold text-vip-black font-semibold rounded-md hover:bg-vip-gold-light transition-colors whitespace-nowrap">
                <i className="ri-calendar-check-line mr-2"></i>Bu Fiyatla Rezervasyon Oluştur
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Price Table with CRUD */}
      <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-vip-gold/15 border border-vip-gold/20 flex items-center justify-center"><i className="ri-table-line text-vip-gold text-xs"></i></div>
            Fiyat Tablosu ({priceTable.length} rota)
          </h3>
        </div>
        {loading ? (
          <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-vip-surface rounded animate-pulse"></div>)}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-vip-gold/10">
                  <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Rota</th>
                  <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Mesafe</th>
                  <th className="text-right py-2.5 px-3 text-gray-400 font-medium">Vito Tek</th>
                  <th className="text-right py-2.5 px-3 text-gray-400 font-medium">E-Sınıfı Tek</th>
                  <th className="text-right py-2.5 px-3 text-gray-400 font-medium">Sprinter Tek</th>
                  <th className="text-right py-2.5 px-3 text-gray-400 font-medium">BMW 7 Tek</th>
                  <th className="text-center py-2.5 px-3 text-gray-400 font-medium">PB</th>
                  <th className="text-center py-2.5 px-3 text-gray-400 font-medium">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {priceTable.map(r => (
                  <tr key={r.id} className="border-b border-vip-gold/5 hover:bg-white/5 transition-colors">
                    <td className="py-2.5 px-3 text-white">{r.rota}</td>
                    <td className="py-2.5 px-3 text-gray-400">{r.mesafe} km</td>
                    <td className="py-2.5 px-3 text-right text-vip-gold">{r.vito_tek}</td>
                    <td className="py-2.5 px-3 text-right text-vip-gold">{r.e_class_tek}</td>
                    <td className="py-2.5 px-3 text-right text-vip-gold">{r.sprinter_tek}</td>
                    <td className="py-2.5 px-3 text-right text-vip-gold">{r.bmw7_tek}</td>
                    <td className="py-2.5 px-3 text-center text-gray-400">{r.para_birimi}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleEdit(r)} className="w-7 h-7 rounded-md hover:bg-vip-gold/10 text-gray-500 hover:text-vip-gold transition-colors flex items-center justify-center">
                          <i className="ri-edit-line text-xs"></i>
                        </button>
                        <button onClick={() => setDeleteConfirm(r)} className="w-7 h-7 rounded-md hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors flex items-center justify-center">
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}